import React, { useMemo } from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, AlertTriangle } from "lucide-react";

import { DEFAULT_ALLOCATION_WEIGHTS, MUST_RESPOND_BY_UNIT_OPTIONS } from "./strategyConstants";
import { scoringKeyForKpi } from "../Analytics/KpiPlatform/kpiCatalogUtils";

const libraryDragType = "kpi-library";
const assignedDragPrefix = "kpi-assigned:";

function LibraryKpiCard({ item, disabled }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${libraryDragType}:${item.id}`,
    data: { source: libraryDragType, kpi: item },
    disabled: Boolean(disabled),
  });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.75 : 1,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm shadow-sm ${
        disabled
          ? "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-400"
          : "border-gray-200 bg-white text-[#0c1629]"
      }`}
    >
      <button
        type="button"
        className={`touch-none shrink-0 ${disabled ? "cursor-not-allowed text-gray-300" : "cursor-grab text-gray-400"}`}
        aria-label={disabled ? `${item.name} (already in strategy)` : `Drag ${item.name}`}
        disabled={disabled}
        {...(disabled ? {} : { ...listeners, ...attributes })}
      >
        <GripVertical size={16} />
      </button>
      <div className="min-w-0 flex-1">
        <span className={`block font-medium ${disabled ? "text-gray-400" : ""}`}>{item.name}</span>
        {item.subtitle ? (
          <span className="mt-0.5 block truncate text-[11px] text-gray-500">{item.subtitle}</span>
        ) : null}
      </div>
      {disabled ? (
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          In use
        </span>
      ) : null}
    </div>
  );
}

function scoringKey(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function AssignedKpiRow({ kpi, strategyId, onWeightChange, onRemove }) {
  const id = `${assignedDragPrefix}${strategyId}:${scoringKeyForKpi(kpi)}`;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-lg border border-gray-200 bg-[#f8fafc] px-3 py-2"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="cursor-grab text-gray-400 touch-none shrink-0"
          aria-label="Reorder KPI"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={16} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center justify-between gap-2 text-sm text-[#0c1629]">
            <span className="truncate font-medium">{kpi.name}</span>
            <span className="shrink-0 font-semibold text-[#2563eb] tabular-nums">{kpi.percent}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            value={kpi.percent}
            onChange={(e) => onWeightChange(strategyId, kpi.name, e.target.value)}
            className="w-full accent-[#3b82f6]"
          />
        </div>
        <button
          type="button"
          className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          onClick={() => onRemove(strategyId, kpi.name)}
          aria-label={`Remove ${kpi.name}`}
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

/**
 * High-visibility validation alert (totals must equal 100%).
 */
function StrategyTotalMismatchAlert({ title, message, total }) {
  return (
    <div
      role="alert"
      aria-live="polite"
      className="mb-4 flex flex-wrap items-stretch gap-0 overflow-hidden rounded-xl border-2 border-amber-500 bg-gradient-to-r from-amber-100 to-orange-50 shadow-lg ring-4 ring-amber-200/60 dark:border-amber-500 dark:from-amber-950/80 dark:to-orange-950/50 dark:ring-amber-900/40"
    >
      <div className="flex min-w-[3.5rem] shrink-0 items-center justify-center bg-amber-500 px-3 py-4 text-white dark:bg-amber-600">
        <AlertTriangle className="drop-shadow-sm" size={28} strokeWidth={2.5} aria-hidden />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-4 py-3 sm:pr-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-amber-900 dark:text-amber-200">
          Warning
        </p>
        <p className="text-base font-bold leading-snug text-amber-950 dark:text-amber-50">{title}</p>
        <p className="text-sm font-medium leading-relaxed text-amber-900/95 dark:text-amber-100/90">
          {message}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-center justify-center border-t border-amber-300/80 bg-white/70 px-5 py-3 sm:border-l sm:border-t-0 dark:border-amber-700 dark:bg-amber-950/40">
        <span className="text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300">
          Current total
        </span>
        <span className="mt-1 font-mono text-2xl font-black tabular-nums leading-none text-red-600 dark:text-red-400">
          {total}%
        </span>
      </div>
    </div>
  );
}

function AllocationWeightWarningBanner({ disabled, rows }) {
  if (disabled) return null;
  const total = (Array.isArray(rows) ? rows : []).reduce(
    (sum, w) => sum + Number(w.weightPct || 0),
    0
  );
  if (total === 100) return null;
  const diff =
    total > 100 ? `${total - 100}% over` : `${100 - total}% short`;
  return (
    <StrategyTotalMismatchAlert
      title="Allocation split does not add up to 100%"
      message={`Percentages for each rank must total exactly 100%. You are ${diff}. Change the ranks yourself before saving this configuration.`}
      total={total}
    />
  );
}

function AssignedKpiDropSurface({ strategyId, hasRows, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: `dropzone-${strategyId}` });
  return (
    <div
      ref={setNodeRef}
      className={`flex min-h-[120px] flex-col gap-2 rounded-lg border border-dashed border-gray-300 bg-[#f8fafc] p-2 ${
        isOver ? "ring-2 ring-[#2563eb]/35" : ""
      }`}
    >
      {!hasRows ? (
        <p className="p-2 text-xs text-gray-500">Drop KPIs here from the library.</p>
      ) : (
        children
      )}
    </div>
  );
}

function AllocationSplitPanel({
  strategyId,
  weights,
  disabled,
  onAllocationWeightChange,
  onAddAllocationRank,
  onRemoveAllocationRank,
}) {
  const rows = Array.isArray(weights) && weights.length ? weights : DEFAULT_ALLOCATION_WEIGHTS;
  const total = rows.reduce((sum, w) => sum + Number(w.weightPct || 0), 0);

  return (
    <div
      className={`mt-3 rounded-lg border border-gray-200 bg-white p-3 ${disabled ? "opacity-90" : ""}`}
    >
      <AllocationWeightWarningBanner disabled={disabled} rows={rows} />
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Allocation split</p>
          <p className="mt-1 max-w-xl text-xs text-gray-500">
            Set the percentage tendered to each performance-ranked position.
          </p>
          {disabled ? (
            <p className="mt-2 text-xs text-[#64748b]">
              Strategy is still loading. Preview shows typical defaults (60 / 30 / 10).
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-3">
          <span
            className={`text-sm font-semibold tabular-nums ${total === 100 ? "text-[#16a34a]" : "text-[#dc2626]"}`}
          >
            Total: {total}%
          </span>
          <button
            type="button"
            disabled={disabled}
            onClick={() => strategyId && onAddAllocationRank?.(strategyId)}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
              disabled
                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                : "bg-[#223688] text-white hover:bg-[#1b2d74]"
            }`}
          >
            + Add Rank
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {rows.map((w) => (
          <div key={w.rank} className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white shadow-sm"
              style={{ backgroundColor: w.color || "#64748b" }}
            >
              {w.rank}
            </div>
            <span className="w-[4.5rem] shrink-0 text-sm font-medium text-[#0c1629]">
              Rank {w.rank}
            </span>
            <div className="min-w-0 flex-1">
              <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full rounded-full transition-[width]"
                  style={{
                    width: `${Math.min(100, Math.max(0, Number(w.weightPct || 0)))}%`,
                    backgroundColor: w.color || "#3b82f6",
                  }}
                />
              </div>
            </div>
            <input
              type="number"
              min={0}
              max={100}
              disabled={disabled}
              className="box-border w-[4.5rem] min-w-[4.5rem] shrink-0 rounded-lg border border-gray-300 px-2 py-1.5 text-right text-sm tabular-nums text-[#0c1629] disabled:bg-gray-50"
              value={w.weightPct}
              onChange={(e) =>
                strategyId && onAllocationWeightChange?.(strategyId, w.rank, e.target.value)
              }
            />
            <button
              type="button"
              disabled={disabled || rows.length <= 1}
              className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:pointer-events-none disabled:opacity-30"
              onClick={() => strategyId && onRemoveAllocationRank?.(strategyId, w.rank)}
              aria-label={`Remove rank ${w.rank}`}
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-4 border-t border-gray-100 pt-3">
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">Split preview</p>
        <div className="flex h-10 w-full overflow-hidden rounded-lg bg-gray-100">
          {rows.map((w) => {
            const pct = Math.max(0, Number(w.weightPct || 0));
            if (pct <= 0) return null;
            return (
              <div
                key={`pv-${w.rank}`}
                className="flex min-h-0 items-center justify-center text-[11px] font-bold text-white"
                style={{
                  flex: `${pct} 1 0`,
                  minWidth: pct > 0 ? "2rem" : 0,
                  backgroundColor: w.color || "#3b82f6",
                }}
                title={`Rank ${w.rank}: ${pct}%`}
              >
                {pct >= 8 ? `${pct}%` : ""}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const StrategyWorkflowBlock = ({
  strategies,
  selectionVariantLibrary,
  kpiLibraryLoading,
  onGoToKpiLibrary,
  onKpiWeightChange,
  onMustRespondChange,
  onAddKpiFromLibrary,
  onRemoveKpi,
  onReorderKpis,
  onAllocationWeightChange,
  onAddAllocationRank,
  onRemoveAllocationRank,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const libraryItems = useMemo(
    () => (Array.isArray(selectionVariantLibrary) ? selectionVariantLibrary : []),
    [selectionVariantLibrary]
  );

  /** Keys already on the active strategy � match by scoring weight key. */
  const assignedKpiKeys = useMemo(() => {
    const first = strategies[0];
    return new Set((first?.kpis || []).map((k) => scoringKeyForKpi(k)));
  }, [strategies]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || !strategies[0]) return;
    const strategy = strategies[0];
    const sid = strategy.id;

    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeId.startsWith(`${libraryDragType}:`)) {
      const libId = activeId.slice(libraryDragType.length + 1);
      const def = libraryItems.find((k) => k.id === libId);
      if (!def) return;
      if (overId === `dropzone-${sid}` || overId.startsWith(`${assignedDragPrefix}${sid}:`)) {
        onAddKpiFromLibrary?.(sid, def);
      }
      return;
    }

    if (
      activeId.startsWith(`${assignedDragPrefix}${sid}:`) &&
      overId.startsWith(`${assignedDragPrefix}${sid}:`)
    ) {
      const keys = (strategy.kpis || []).map((k) => scoringKeyForKpi(k));
      const activeKey = activeId.split(":").pop();
      const overKey = overId.split(":").pop();
      const oldIndex = keys.indexOf(activeKey);
      const newIndex = keys.indexOf(overKey);
      if (oldIndex >= 0 && newIndex >= 0 && oldIndex !== newIndex) {
        onReorderKpis?.(sid, arrayMove(keys, oldIndex, newIndex));
      }
    }
  };

  const kpiTotal = (strategy) =>
    (strategy?.kpis || []).reduce((sum, kpi) => sum + Number(kpi.percent || 0), 0);

  function KpiTotalWarningBanner({ strategy }) {
    const kpis = strategy?.kpis || [];
    if (!kpis.length) return null;
    const total = kpiTotal(strategy);
    if (total === 100) return null;
    const diff =
      total > 100 ? `${total - 100}% over` : `${100 - total}% short`;
    return (
      <StrategyTotalMismatchAlert
        title="KPI weights do not add up to 100%"
        message={`All KPIs in the strategy list must total exactly 100%. You are ${diff} � adjust the sliders or change which KPIs are included before saving.`}
        total={total}
      />
    );
  }

  return (
    <div className="relative rounded-xl border border-gray-200 bg-white p-fluid-3">
      <div className="mb-fluid-2">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
          Current Strategy Workflow <span className="text-red-500">*</span>
        </p>
        <p className="mt-1 text-sm text-gray-500">
          Default tender strategy. Drag KPIs from the library and set weights (total 100%).
        </p>
      </div>

      <div className="space-y-fluid-2">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          {strategies.length === 0 ? (
            <>
              <div className="rounded-xl border border-dashed border-gray-300 bg-[#fbfbfd] p-10 text-center text-sm text-gray-500">
                Strategy is loading. If this persists, refresh the page.
              </div>
              <AllocationSplitPanel
                strategyId={null}
                weights={DEFAULT_ALLOCATION_WEIGHTS}
                disabled
                onAllocationWeightChange={onAllocationWeightChange}
                onAddAllocationRank={onAddAllocationRank}
                onRemoveAllocationRank={onRemoveAllocationRank}
              />
            </>
          ) : (
            strategies.map((strategy) => {
              const assignedSortableIds = (strategy.kpis || []).map(
                (k) => `${assignedDragPrefix}${strategy.id}:${scoringKeyForKpi(k)}`
              );
              return (
              <div key={strategy.id} className="rounded-xl border border-gray-200 bg-[#f6f7fb] p-fluid-2">
                <div className="min-w-0 flex-1">
                    <div>
                      <p className="text-fluid-base font-semibold text-[#0c1629]">{strategy.title}</p>
                      <p className="text-sm text-gray-500">{strategy.subtitle}</p>
                    </div>

                    <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
                      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                        Response Window <span className="text-red-500">*</span>
                      </p>
                      <p className="mb-3 text-xs text-gray-500">
                        Carriers must respond to a tender within this window before it advances.
                      </p>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500">
                            Must Respond By Value <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            min={1}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            value={strategy.mustRespondByValue ?? ""}
                            onChange={(e) =>
                              onMustRespondChange?.(strategy.id, {
                                value:
                                  e.target.value === "" ? "" : Math.max(1, Number(e.target.value)),
                              })
                            }
                            placeholder="e.g. 72"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500">
                            Must Respond By Unit <span className="text-red-500">*</span>
                          </label>
                          <select
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            value={strategy.mustRespondByUnit || "hours"}
                            onChange={(e) =>
                              onMustRespondChange?.(strategy.id, { unit: e.target.value })
                            }
                          >
                            {MUST_RESPOND_BY_UNIT_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                        Selection KPI library & weights
                      </p>
                      <p className="mb-3 text-xs text-gray-500">
                        Drag selection KPIs (fixed windows) into the active list. Each variant maps
                        to a distinct scoring weight key. Percentages must sum to 100%.
                      </p>
                      {kpiLibraryLoading ? (
                        <p className="mb-2 text-xs text-gray-500">Loading KPI catalog�</p>
                      ) : null}

                      <KpiTotalWarningBanner strategy={strategy} />

                      <div className="mb-2 text-xs font-semibold">
                        KPI total:{" "}
                        <span
                          className={`tabular-nums ${kpiTotal(strategy) === 100 ? "text-[#16a34a]" : "text-[#dc2626]"}`}
                        >
                          {kpiTotal(strategy)}%
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <div>
                          <p className="mb-2 text-[11px] font-bold uppercase text-gray-500">
                            Available selection KPIs
                          </p>
                          <div className="flex min-h-[120px] flex-col gap-2 rounded-lg border border-dashed border-gray-200 bg-[#fafafa] p-2">
                            {kpiLibraryLoading ? (
                              <p className="p-2 text-xs text-gray-500">Loading KPI library�</p>
                            ) : libraryItems.length ? (
                              libraryItems.map((item) => (
                                <LibraryKpiCard
                                  key={item.id}
                                  item={item}
                                  disabled={assignedKpiKeys.has(
                                    item.scoringWeightKey || scoringKey(item.name)
                                  )}
                                />
                              ))
                            ) : (
                              <div className="p-2 text-xs text-gray-500">
                                <p>No selection KPIs in the library yet.</p>
                                {onGoToKpiLibrary ? (
                                  <button
                                    type="button"
                                    className="mt-1 font-semibold text-[#223688] underline"
                                    onClick={onGoToKpiLibrary}
                                  >
                                    Open KPI Library tab to add variants
                                  </button>
                                ) : null}
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <p className="mb-2 text-[11px] font-bold uppercase text-gray-500">
                            In strategy (drag to reorder)
                          </p>
                          <SortableContext
                            items={assignedSortableIds}
                            strategy={verticalListSortingStrategy}
                          >
                            <AssignedKpiDropSurface
                              strategyId={strategy.id}
                              hasRows={Boolean((strategy.kpis || []).length)}
                            >
                              {(strategy.kpis || []).map((kpi) => (
                                <AssignedKpiRow
                                  key={scoringKeyForKpi(kpi)}
                                  kpi={kpi}
                                  strategyId={strategy.id}
                                  onWeightChange={onKpiWeightChange}
                                  onRemove={onRemoveKpi}
                                />
                              ))}
                            </AssignedKpiDropSurface>
                          </SortableContext>
                        </div>
                      </div>
                    </div>

                    <AllocationSplitPanel
                      strategyId={strategy.id}
                      weights={strategy.allocationWeights}
                      onAllocationWeightChange={onAllocationWeightChange}
                      onAddAllocationRank={onAddAllocationRank}
                      onRemoveAllocationRank={onRemoveAllocationRank}
                    />
                </div>
              </div>
            );
            })
          )}
        </DndContext>
      </div>
    </div>
  );
};

export default StrategyWorkflowBlock;
